import React from 'react';
import {appCtx, isAWS, isOnPrem, isTalDemo, useFetchData} from '../app/AppContext';
import {DashCardDivider} from '../app/CommonUIComponenets';
import {ReactComponent as RemedioLogoWhite} from '../img/Remedio-White-Logo.svg';
import {ReactComponent as RemedioLogoColor} from '../img/Remedio-Color-Logo.svg';
import {useFeatureFlags, useUser} from '../app/UseAppData';
import {checkRoleAdmin} from '../app/CommonUIData';
import {findNotifPath, MenuTree} from '../tools/UIControls';
import {useInputChange} from '../tools/Inputs';
import {useColorModeTheme} from '../tools/CommonReact';
import {socketNotifs} from '../app/Exports';
import {mockData} from '../app/MockData';
import {useModuleDisplay} from '../app/UseModuleDisplay.js';
import {ROUTES} from '../routePaths.js';
import {stripV3} from '../v3Bridge';

export function SideBar({menu, helpActions}) {
	const {user} = useUser(), isAdmin = checkRoleAdmin(user?.perms?.[0]);
	const {curTheme} = useColorModeTheme();
	const {rsp: ftrFlags, isLoading: isFFLoading} = useFeatureFlags();
	const {isModuleAllowed, shouldDisplay} = useModuleDisplay();

	const {inp, onChange} = useInputChange({help: ''});

	const {rsp: rspNotifs} = useFetchData({
		api: '/api/v2/notif_get-totals',
		socket: socketNotifs,
		alt: mockData.notifTotals
	});

	const isAllSubRejected = (item) => item.subMenu && item.subMenu.every(subItem => !isModuleAllowed(subItem.perms?.module));

	const isTabAllowed = (item) => {
		const isAllowed = isModuleAllowed(item.perms?.module);
		if (isAllowed && item.subMenu)
			return !isAllSubRejected(item);

		return isAllowed;
	};

	const filterMenuByPerms = (menu) => menu
		.filter(item =>
			(!item.perms?.admin || isAdmin) &&
			(!item.perms?.groupIds || isAdmin || user?.perms?.some(perm => item.perms.groupIds.includes(perm.groupId))) &&
			(!item.perms?.envs || item.perms.envs.every(env => {
				const isNegative = env.startsWith('!');
				const envKey = isNegative ? env.substring(1) : env;
				const envValue = {onprem: isOnPrem, saas: isAWS, demo: !!ftrFlags?.ff_demo?.demoEnabled}[envKey];
				return isNegative ? !envValue : envValue;
			})) // Allows perms.env to specify "onprem", "demo", or "saas", or negative with "!onprem".
		)
		.filter(item => !ftrFlags?.ff_license?.hideUnlicensedModules || (!isAllSubRejected(item) && shouldDisplay(item.perms?.module)))
		.map(item => ({
			...item,
			...(!isTabAllowed(item) && {disabled: true, tip: 'Unlicensed - contact Remedio for more information.'}),
			...(item.subMenu && {
				subMenu: filterMenuByPerms(item.subMenu)
			}),
		}));

	const filteredMenu = filterMenuByPerms(menu, isAdmin);

	const notifPaths = rspNotifs?.data.map(nt => findNotifPath(filteredMenu, nt.type, nt.count)).filter(Boolean);

	const onSelect = (k, params, itm) => itm?.a? itm.a() : appCtx.nav.to(k, params || {}, {isReset: true});

	return (
		<div className='flex-none sticky top-0 w-60 max-sm:w-16 px-4 z-10 shadow-lg py-5 overflow-y-scroll scrollbar-none h-screen flex flex-col bg-bg3'>
			{React.createElement(curTheme !== 'dark'? RemedioLogoColor : RemedioLogoWhite, {
				className: 'shrink-0 fill-title w-4/5 max-sm:w-68 max-sm:overflow-hidden max-sm:self-start min-sm:self-center cursor-pointer',
				onClick: () => appCtx.nav.to(ROUTES.MISCON_OVERVIEW, {}, {isReset: true})
			})}
			{!isFFLoading && (
				<MenuTree
					menuArr={filteredMenu} field='page' notifsPaths={notifPaths} value={stripV3(appCtx.nav.getMenuPath())}
					className='text-icon' onSelect={onSelect} canSelectHead subMenuOpenPred={(subOpen, path) => subOpen?.startsWith(path)}/>
			)}
			<DashCardDivider/>
			<div className='grow'/>
			{isTalDemo && helpActions &&
				<MenuTree
					menuArr={helpActions} field='help' onChange={onChange} value={inp.help}
					className='shadow-md w-48 bottom-4 rounded-lg bg-bg1 text-title' openAbove depth={1}/>}
			{import.meta.env.DEV &&
				<button
					type='button' onClick={() => appCtx.nav.to(ROUTES.DESIGN_SYSTEM, {}, {isReset: true})}
					className='text-prm-light text-xs mt-3 cursor-pointer hover:underline text-left'>
					<span className='font-mono'>&lt;/&gt;</span> Design System
				</button>}
			<a rel='noreferrer' target='_blank' href={'http://www.gytpol.com/open-source-notice'} className='text-prm-light text-xs mt-3 cursor-pointer hover:underline'>
				Third-Party Notice <i className=' icon-arrow-forward-outline'/>
			</a>
		</div>
	);
}
